/** @jsx jsx */
import { Flex, Label } from '@bottlebooks/gatsby-design-system';
import { jsx } from 'theme-ui';
import { useLingui } from '@lingui/react';
import { graphql } from 'gatsby';

export default function MarketingActivityBadge({ type, variant, className }) {
  const { i18n } = useLingui();
  return (
    <Flex sx={{ alignItems: 'center' }} className={className}>
      <MarketingActivityIcon
        type={type}
        variant={variant}
        sx={{ paddingRight: 4 }}
      />
      <Label sx={{ paddingX: 2 }}>{i18n._(`marketingActivity.${type}`)}</Label>
    </Flex>
  );
}

const iconVariants = {
  onDark: { sx: { color: 'onPrimary' } },
  onPrimary: { sx: { color: 'primary' }, backgroundColor: '#fff' },
  default: { sx: { color: 'primary' }, backgroundColor: 'red' },
};

export function MarketingActivityIcon({ type, variant }) {
  const Icon = icons[type] || icons.default;
  return <Icon {...(iconVariants[variant] || iconVariants.default)} />;
}

const icons = {
  default: VirtualTastingIcon,
  virtualTasting: VirtualTastingIcon,
  virtualTastings: VirtualTastingIcon,
  localEvent: LocalEventIcon,
  inDusseldorf: PhysicalEventIcon,
  inVerona: PhysicalEventIcon,
  onSite: PhysicalEventIcon,
};

function VirtualTastingIcon({ backgroundColor = '#fff', ...rest }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="28"
      height="29"
      fill="none"
      viewBox="0 0 28 29"
      {...rest}
    >
      <rect
        width="24"
        height="24"
        x="1.479"
        y="2.987"
        fill={backgroundColor}
        rx="12"
      ></rect>
      <path
        fill="currentColor"
        fillOpacity="0.5"
        fillRule="evenodd"
        d="M9.423 12.671a3.71 3.71 0 00.724 4.62c1.522 1.37 4.049 1.094 5.465-.39 1.26-1.319 1.74-2.201 2.23-3.271-2.14.776-4.946.456-8.42-.959z"
        clipRule="evenodd"
      ></path>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M20.795 11.258c-1.069 2.285-2.522 4.7-3.409 5.708-1.714 1.948-4.6 2.123-6.452.418-1.858-1.712-1.99-4.687-.27-6.64.883-1.005 3.07-2.729 5.173-4.053l4.958 4.567zm.915.403a.971.971 0 00-.22-1.123l-5.004-4.61a.945.945 0 00-1.145-.106c-2.144 1.346-4.445 3.144-5.428 4.26-2.017 2.292-1.934 5.775.186 7.886l-3.168 3.52-2.104-1.895a.5.5 0 00-.67.743l4.73 4.258a.5.5 0 00.669-.743l-1.882-1.694 3.195-3.55c2.262 1.555 5.375 1.171 7.268-.98.982-1.117 2.49-3.644 3.573-5.966z"
        clipRule="evenodd"
      ></path>
    </svg>
  );
}

function LocalEventIcon({ backgroundColor = '#fff', ...rest }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="28"
      height="29"
      fill="none"
      viewBox="0 0 28 29"
      {...rest}
    >
      <rect
        width="27.729"
        height="28.008"
        fill={backgroundColor}
        rx="13.865"
      ></rect>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M22.08 21.119H5.648V9.524c0-.58.46-1.054 1.027-1.054h1.027v1.054c0 .291.23.527.514.527a.52.52 0 00.513-.527V8.47H19v1.054c0 .291.23.527.514.527a.52.52 0 00.513-.527V8.47h1.027c.567 0 1.027.473 1.027 1.054V21.12zM21.053 7.416h-1.027V6.362a.52.52 0 00-.513-.527.52.52 0 00-.514.527v1.054H8.73V6.362a.52.52 0 00-.513-.527.52.52 0 00-.514.527v1.054H6.675c-1.133 0-2.054.945-2.054 2.108v12.649h18.486V9.524c0-1.163-.921-2.108-2.054-2.108zm-2.567 4.216a.52.52 0 00-.514.527c0 .291.23.527.514.527a.52.52 0 00.513-.527.52.52 0 00-.513-.527zm-3.081 0a.52.52 0 00-.514.527c0 .291.23.527.514.527a.52.52 0 00.513-.527.52.52 0 00-.513-.527zm-3.081 0a.52.52 0 00-.514.527c0 .291.23.527.514.527a.52.52 0 00.513-.527.52.52 0 00-.513-.527zm-3.081 6.324a.52.52 0 00-.514.527c0 .291.23.527.514.527a.52.52 0 00.513-.527.52.52 0 00-.513-.527zm6.162 0a.52.52 0 00-.514.527c0 .291.23.527.514.527a.52.52 0 00.513-.527.52.52 0 00-.513-.527zm-3.081 0a.52.52 0 00-.514.527c0 .291.23.527.514.527a.52.52 0 00.513-.527.52.52 0 00-.513-.527zm-3.081-3.162a.52.52 0 00-.514.527c0 .291.23.527.514.527a.52.52 0 00.513-.527.52.52 0 00-.513-.527zm9.243 0a.52.52 0 00-.514.527c0 .291.23.527.514.527a.52.52 0 00.513-.527.52.52 0 00-.513-.527zm-3.081 0a.52.52 0 00-.514.527c0 .291.23.527.514.527a.52.52 0 00.513-.527.52.52 0 00-.513-.527zm-3.081 0a.52.52 0 00-.514.527c0 .291.23.527.514.527a.52.52 0 00.513-.527.52.52 0 00-.513-.527z"
        clipRule="evenodd"
      ></path>
    </svg>
  );
}

function PhysicalEventIcon({ backgroundColor = '#fff', ...rest }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="29"
      height="29"
      fill="none"
      viewBox="0 0 29 29"
      {...rest}
    >
      <rect
        width="27.729"
        height="28.008"
        x="0.521"
        fill={backgroundColor}
        rx="13.865"
      ></rect>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M14.385 4.668c3.19 0 5.777 3.25 5.777 7.261 0 2.674-1.925 6.477-5.777 11.41-3.851-4.933-5.777-8.736-5.777-11.41 0-4.01 2.587-7.261 5.777-7.261zm0 3.112c-1.595 0-2.888 1.393-2.888 3.112s1.293 3.112 2.888 3.112c1.596 0 2.889-1.393 2.889-3.112S15.98 7.78 14.385 7.78z"
        clipRule="evenodd"
      ></path>
    </svg>
  );
}

export const fragment = graphql`
  fragment MarketingActivityBadge on MarketingActivity {
    type
  }
`;
